import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";

const DEFAULT_TYPE_LABELS = {
  preference: "偏好/喜好",
  personality: "性格特点",
  bottom_line: "婚恋底线",
  lifestyle: "生活方式",
  relationship: "关系特点",
  other: "其他",
};

export default function AiMemoryPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [items, setItems] = useState([]);
  const [typeLabels, setTypeLabels] = useState(DEFAULT_TYPE_LABELS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");
  const [memoryType, setMemoryType] = useState("preference");
  const [importance, setImportance] = useState(6);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [filterType, setFilterType] = useState("all");

  useSeo({
    title: "我的AI记忆 | 用户中心",
    metaDescription: "查看和管理 AI 咨询师与数字分身记住的关于你的信息。",
    path: "/center/ai-memory",
    noindex: true,
  });

  useEffect(() => {
    if (!userId) {
      navigate("/login?redirect=/center/ai-memory", { replace: true });
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { res, data } = await apiJson(`/api/ai/memory?user_id=${userId}`);
      if (!res.ok) throw new Error(data?.detail || "加载失败");
      setItems(data.items || []);
      if (data.type_labels) setTypeLabels(data.type_labels);
    } catch (err) {
      alert(err.message || "加载 AI 记忆失败");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e?.preventDefault();
    const text = content.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      const { res, data } = await apiJson("/api/ai/memory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          memory_type: memoryType,
          memory_content: text,
          importance: Number(importance) || 5,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "添加失败");
      setContent("");
      await load();
    } catch (err) {
      alert(err.message || "添加失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("确定删除这条记忆？AI 之后将不再使用它。")) return;
    try {
      const { res, data } = await apiJson(
        `/api/ai/memory/${id}?user_id=${userId}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(data?.detail || "删除失败");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      alert(err.message || "删除失败");
    }
  };

  const handleSaveEdit = async (id) => {
    const text = editContent.trim();
    if (!text) return;
    try {
      const { res, data } = await apiJson(`/api/ai/memory/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, memory_content: text }),
      });
      if (!res.ok) throw new Error(data?.detail || "保存失败");
      setEditingId(null);
      await load();
    } catch (err) {
      alert(err.message || "保存失败");
    }
  };

  const labelOf = (type) => typeLabels[type] || type || "其他";
  const filteredItems =
    filterType === "all"
      ? items
      : items.filter((x) => x.memory_type === filterType);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">我的 AI 记忆</h2>
          <p className="text-sm text-gray-500 mt-1">
            AI 咨询师与数字分身会记住你的喜好、情绪与关系变化，用于更懂你的回复。可随时修改或删除。
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link to="/ai/twin" className="text-rose-600 hover:underline">
            数字分身
          </Link>
          <Link to="/ai/personality" className="text-rose-600 hover:underline">
            AI 人格
          </Link>
          <Link to="/ai/consultant" className="text-rose-600 hover:underline">
            AI 咨询师 →
          </Link>
        </div>
      </div>

      {!loading && items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilterType("all")}
            className={`px-3 py-1 rounded-full text-xs border ${
              filterType === "all"
                ? "bg-rose-500 text-white border-rose-500"
                : "bg-white text-gray-600 border-gray-200"
            }`}
          >
            全部 ({items.length})
          </button>
          {Object.entries(typeLabels).map(([key, label]) => {
            const count = items.filter((x) => x.memory_type === key).length;
            if (!count) return null;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilterType(key)}
                className={`px-3 py-1 rounded-full text-xs border ${
                  filterType === key
                    ? "bg-rose-500 text-white border-rose-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
      )}

      <form
        onSubmit={handleCreate}
        className="bg-rose-50/60 border border-rose-100 rounded-2xl p-4 space-y-3"
      >
        <div className="text-sm font-semibold text-gray-800">添加一条记忆</div>
        <div className="flex flex-wrap gap-2">
          <select
            value={memoryType}
            onChange={(e) => setMemoryType(e.target.value)}
            className="border rounded-xl px-3 py-2 text-sm bg-white"
          >
            {Object.entries(typeLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={importance}
            onChange={(e) => setImportance(e.target.value)}
            className="border rounded-xl px-3 py-2 text-sm bg-white"
            title="重要度"
          >
            {[...Array(10)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                重要度 {i + 1}
              </option>
            ))}
          </select>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="例如：不能接受长期异地；喜欢周末户外运动；沟通时需要先冷静再谈…"
          className="w-full border rounded-xl px-3 py-2 text-sm bg-white"
        />
        <button
          type="submit"
          disabled={saving || !content.trim()}
          className="px-4 py-2 rounded-full bg-rose-500 text-white text-sm disabled:opacity-50"
        >
          {saving ? "保存中…" : "添加记忆"}
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">加载中…</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed rounded-2xl p-8 text-center text-gray-500 text-sm">
          暂无 AI 记忆。可手动添加，或与 AI 咨询师 / 数字分身聊天后自动生成。
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="border border-dashed rounded-2xl p-8 text-center text-gray-500 text-sm">
          当前类型暂无记忆。
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="border border-gray-100 rounded-2xl p-4 bg-white shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">
                    {labelOf(item.memory_type)}
                  </span>
                  <span className="text-gray-400">重要度 {item.importance}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-400">
                    {item.source === "manual"
                      ? "手动"
                      : item.source === "profile"
                        ? "画像同步"
                        : item.source === "twin_chat"
                          ? "分身对话"
                          : "对话提取"}
                  </span>
                </div>
                <div className="flex gap-2 text-xs">
                  {editingId === item.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(item.id)}
                        className="text-rose-600"
                      >
                        保存
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="text-gray-400"
                      >
                        取消
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(item.id);
                          setEditContent(item.memory_content || "");
                        }}
                        className="text-gray-500 hover:text-rose-600"
                      >
                        编辑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500"
                      >
                        删除
                      </button>
                    </>
                  )}
                </div>
              </div>
              {editingId === item.id ? (
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={3}
                  className="w-full border rounded-xl px-3 py-2 text-sm"
                />
              ) : (
                <p className="text-sm text-gray-800 leading-relaxed">
                  {item.memory_content}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
